import React, { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';

import { useNoticeBannersStackActions, useToggleState } from '@sg/garnish';
import { ReorderRail } from '@sg/shared-fe-features-menu';

import { useTelemetry } from '@order/Telemetry';

import { type useMenuReorderRailOrders } from '../../../../hooks/useMenuReorderRailOrders';
import { useReorderOrderLineItem } from '../../../../hooks/useReorderOrderLineItem';
import { ReorderConflictModal } from '../../../ReorderConflictModal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuReorderRailCard = (props: Props) => {
  const {
    location = 'menu',
    lineItem,
    mostRecentOrder,
    targetRestaurant,
    disableAnimation,
  } = props;

  const { push: addBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── Refs ────────────────────────────────────────────────────────────

  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isConflictReviewModalVisible,
    toggleOn: showConflictReviewModal,
    toggleOff: hideConflictReviewModal,
  } = useToggleState(false);

  const [status, setStatus] = useState<ReorderRailCardStatus>('idle');

  // ─── Helpers ─────────────────────────────────────────────────────────

  const clearSuccessTimeout = useCallback(() => {
    if (!successTimeoutRef.current) return;

    clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = undefined;
  }, []);

  const onSuccessfulReorder = useCallback(() => {
    hideConflictReviewModal();

    addBanner({
      palette: 'success',
      text: formatMessage(messages.successfulReorderMessage, {
        quantity: 1,
        product: lineItem.product.name,
      }),
      autoHideTimeout: 2500,
    });

    clearSuccessTimeout();
    setStatus('success');

    successTimeoutRef.current = setTimeout(() => {
      setStatus('idle');
    }, SUCCESS_DISPLAY_DURATION_MS);
  }, [
    addBanner,
    formatMessage,
    lineItem.product.name,
    hideConflictReviewModal,
    clearSuccessTimeout,
  ]);

  const {
    reorderLineItem,
    isFetching: isReordering,
    unavailableIngredients,
    targetLocation,
  } = useReorderOrderLineItem({
    variation: 'order',
    location,
    productId: lineItem.product.id,
    productCost: undefined,
    productName: lineItem.product.name,
    mostRecentOrder,
    targetRestaurant,
    onUnavailableIngredientConflict: showConflictReviewModal,
    onSuccess: onSuccessfulReorder,
  });

  const handlePress = useCallback(async () => {
    clearSuccessTimeout();
    setStatus('loading');
    track('reorder.line_item.tapped', { location });

    await reorderLineItem(lineItem.id);

    setStatus((current) => (current === 'loading' ? 'idle' : current));
  }, [reorderLineItem, lineItem.id, track, clearSuccessTimeout, location]);

  const reorderLineItemForce = useCallback(async () => {
    await reorderLineItem(lineItem.id, true);
  }, [lineItem.id, reorderLineItem]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => clearSuccessTimeout, [clearSuccessTimeout]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <ReorderRail.Card
        lineItemName={lineItem.product.name}
        imageUrl={lineItem.product.asset?.url}
        lastOrderedAt={lineItem.order.wantedTime}
        status={status}
        disabled={isReordering}
        disableAnimation={disableAnimation}
        onPress={handlePress}
      />

      <ReorderConflictModal
        visible={isConflictReviewModalVisible}
        lineItem={lineItem}
        isReordering={isReordering}
        onRequestClose={hideConflictReviewModal}
        addToBagAnyway={reorderLineItemForce}
        targetLocation={targetLocation}
        unavailableIngredients={unavailableIngredients}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly successfulReorderMessage: {
    readonly product: MessageValue;
    readonly quantity: MessageValue;
  };
}>({
  successfulReorderMessage: {
    defaultMessage: '{quantity}x {product} added to your bag!',
    description: 'Reorder | Successful reorder message',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SUCCESS_DISPLAY_DURATION_MS = 2000;

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  location?: 'home' | 'menu';
  lineItem: ReturnType<typeof useMenuReorderRailOrders>['mostRecentLineItems'][number];
  mostRecentOrder: UseReorderOrderLineItemParams['mostRecentOrder'];
  targetRestaurant: UseReorderOrderLineItemParams['targetRestaurant'];
  disableAnimation?: boolean;
};

type ReorderRailCardStatus = NonNullable<ComponentProps<typeof ReorderRail.Card>['status']>;
type UseReorderOrderLineItemParams = Parameters<typeof useReorderOrderLineItem>[0];
